import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { InputOption } from '@app/models/input-option';
import { SelectionsModel } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { LimitsInputComponent } from '@app/shared/inputs/limits-input/limits-input.component';
import { RadioInputGroupComponent } from '@app/shared/inputs/radio-input-group/radio-input-group.component';
import { VennHealthHelpers } from '@app/shared/vennHealthHelpers';
import { first, skip, asapScheduler } from 'rxjs';

@Component({
  selector: 'app-albumin',
  templateUrl: './albumin.component.html',
  styleUrls: ['./albumin.component.css']
})
export class AlbuminComponent implements OnInit {

  @ViewChild('childMeasurementComponent') childMeasurementComponent: RadioInputGroupComponent;
  @ViewChild('childLimitsComponent') childLimitsComponent: LimitsInputComponent;

  @Input() indication: IndicationDefinition;
  albuminMeasurementIndication: IndicationDefinition;
  albuminLimitsIndication: IndicationDefinition;
  
  allIndications: IndicationGroup[] = [];

  childMeasurementSelectionModel: SelectionsModel;
  childLimitsSelectionModel: SelectionsModel[];

  lowerLimitsVariableName = 'lowerLimits';
  upperLimitsVariableName = 'upperLimits';

  albuminInputOptions: InputOption[][] = [
    // measurement inputs
    [
      {name: 'urine', displayName: 'Albumin, urine (ug/mL)', order: 1, defaultValue: 'serum'},
      {name: 'serum', displayName: 'Albumin, serum (g/dL)', order: 2, defaultValue: 'serum'}
    ],
    // limits inputs
    [
      {name: this.lowerLimitsVariableName, displayName: 'Lower Limits', order: 1, defaultValue: 3.4, maxlength: 5, details: '(>=)'},
      {name: this.upperLimitsVariableName, displayName: 'Upper Limits', order: 2, defaultValue: 5.5, maxlength: 5, details: '(<)'}
    ]
  ];

  constructor(
    private sharedService: SharedService
  ) { }

  ngOnInit() {

    this.albuminMeasurementIndication = Object.assign({}, this.indication);
    this.albuminMeasurementIndication.inputOptions = this.albuminInputOptions[0];

    this.albuminLimitsIndication = Object.assign({}, this.indication);
    this.albuminLimitsIndication.inputOptions = this.albuminInputOptions[1];

    // only called first time to setup form and update selected filters via shared service
    this.sharedService.allIndications.pipe(first()).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.setIndicationSelections();
      }
    });
    // called everytime after the first time
    this.sharedService.allIndications.pipe(skip(1)).subscribe(data => {
      if (data) {
        this.allIndications = data;
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data) {
        const loadedIndication = VennHealthHelpers.getCurrentIndicationFromIndicationGroupJson(this.indication, this.allIndications);
        this.indication.selections = loadedIndication.selections;
        this.indication.selected = loadedIndication.selected;

        this.setIndicationSelections();
      }
    });
  }

  setIndicationSelections() {
    if (this.indication.selected && this.indication.selections.length > 0) {
      asapScheduler.schedule(() => {
        if (this.indication.selections.some(i => i.controlName === 'measurement')) {
          this.childMeasurementComponent.overrideInputValue(this.indication.selections.find(i => i.controlName === 'measurement').selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === this.lowerLimitsVariableName)) {
          this.childLimitsComponent.overrideInputValue(this.lowerLimitsVariableName, this.indication.selections.find(i => i.controlName === this.lowerLimitsVariableName).selectionValue);
        }
        if (this.indication.selections.some(i => i.controlName === this.upperLimitsVariableName)) {
          this.childLimitsComponent.overrideInputValue(this.upperLimitsVariableName, this.indication.selections.find(i => i.controlName === this.upperLimitsVariableName).selectionValue);
        }
        this.updateSelections();
      });
    } else {
      this.setDefaults();
    }
  }

  childSharedRadioControlSelectionChangedHandler(selectionModel: SelectionsModel) {
    this.childMeasurementSelectionModel = selectionModel;
    this.updateSelections();
  }

  childSharedTextControlSelectionChangedHandler(selections: SelectionsModel[]) {
    this.childLimitsSelectionModel = selections;
    this.updateSelections();
  }

  setDefaults() {
    asapScheduler.schedule(() => {
      this.childMeasurementComponent.overrideInputValue('serum');
      this.childLimitsComponent.overrideInputValue(this.lowerLimitsVariableName, this.albuminInputOptions[1].find(i => i.name === this.lowerLimitsVariableName).defaultValue);
      this.childLimitsComponent.overrideInputValue(this.upperLimitsVariableName, this.albuminInputOptions[1].find(i => i.name === this.upperLimitsVariableName).defaultValue);
      this.updateSelections();
    });
  }

  updateSelections(): void {
    let currentSelections: SelectionsModel[] = [];

    if (this.childMeasurementSelectionModel) {
      currentSelections.push(this.childMeasurementSelectionModel);
    }

    if (this.childLimitsSelectionModel) {
      currentSelections = currentSelections.concat(this.childLimitsSelectionModel);
    }
    
    this.indication.selections = currentSelections;
    const currentIndicationGroup = VennHealthHelpers.getIndicationGroup(this.indication);
    const matchingGroupIndex = this.allIndications.findIndex(i => i.name === currentIndicationGroup.name);
    const matchingIndicationIndex = this.allIndications[matchingGroupIndex].indications.findIndex(i => i.name === this.indication.name);
    this.allIndications[matchingGroupIndex].indications[matchingIndicationIndex] = this.indication;
    this.sharedService.setAllIndications(this.allIndications);
  }

}
